import React, { useEffect, useRef, useState } from "react";
import BgBox from "../../../common/BgBox";
import HeaderText from "../../../common/HeaderText";
import CardUI from "../../../common/Card";
import { Box, Grid, TextField, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AlertMessage from "../../../common/AlertMessage";
import { otpVerifyFunc, resetOtpVerify } from "../../../actions/Auth";
import MccLogo from "../../../components/MccLogo";
import { LoadingButton } from "@mui/lab";
import styled from "@emotion/styled";

const useStyles = styled((theme) => ({
  otp_box: {
    "& input": {
      fontSize: "22px !important",
      padding: "12px",
      textAlign: "center",
      WebkitAppereance: "none",
    },
    "& input::-webkit-outer-spin-button, input::-webkit-inner-spin-button": {
      display: "none",
      margin: 0,
    },
  },
}));
let currentOtpIndex = 0;
const numbers = /^[0-9\b]+$/;

const OtpVerification = () => {
  const classes = useStyles();

  const [otp, setOtp] = useState(new Array(6).fill(""));
  // const [finalOtp, setFinalOtp] = useState("");
  const [activeOtpIndex, setActiveOtpIndex] = useState(0);

  const inputRef = useRef();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const otpVerify = useSelector((state) => state.otpVerify);
  const { loading, error, res } = otpVerify;

  const [open, setOpen] = useState(false);
  const [moveToNext, setMoveToNext] = useState(false);

  const location = useLocation();

  // const state = location.state && location.state

  useEffect(() => {
    if (!error && res?.ok && location?.state?.createAgency && moveToNext) {
      dispatch(resetOtpVerify());
      navigate("/create-password", { state: { agent_data: res } });
      return;
    }
  }, [res]);

  useEffect(() => {
    if (
      !error &&
      res?.ok &&
      location?.state?.createAgency &&
      location?.state?.fromCareGroup
    ) {
      dispatch(resetOtpVerify());
      navigate("/join-workspace", {
        state: {
          agent_data: res,
          existing_agencies: res?.agent?.existing_agencies,
        },
      });
      return;
    }
  }, [res]);

  useEffect(() => {
    if (!error && res?.ok && location?.state?.forgotPassword && moveToNext) {
      dispatch(resetOtpVerify());
      navigate("/reset-password", {
        state: { finalEmail: location?.state.email },
      });
      return;
    }
  }, [res, location]);

  useEffect(() => {
    if (location?.state === null) {
      navigate(-1);
    }
  }, [res, location, navigate]);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  const otpVerificationHandler = async (e) => {
    e.preventDefault();

    const otpNum = otp.join("");

    if (!otpNum.length) {
      return;
    }

    // if (location?.state?.fromCareGroup) {
    //   navigate("/join-workspace", { state: { agent_data: res } });
    // } else {
    //   navigate("/create-password", { state: { agent_data: res } });
    // }

    dispatch(resetOtpVerify());
    dispatch(otpVerifyFunc(location?.state?.email, otpNum));
    setOpen(true);
    setMoveToNext(true);
  };

  const otpChangeHandler = (e) => {
    const { value } = e.target;

    const newOtp = [...otp];

    if (value === "" || numbers.test(value)) {
      newOtp[currentOtpIndex] = value.substring(value.length - 1);
      if (!value) {
        setActiveOtpIndex(currentOtpIndex - 1);
      } else {
        // pasteOtpHandler();
        setActiveOtpIndex(currentOtpIndex + 1);
      }
    }
    setOtp(newOtp);
  };

  const keyDownHandler = (e, index) => {
    currentOtpIndex = index;
    if (e.key === "Backspace") {
      setActiveOtpIndex(currentOtpIndex - 1);
    }
  };

  const pasteOtpHandler = (e) => {
    e.preventDefault();
    const pasteText = (e.clipboardData || window.clipboardData)
      .getData("text/plain")
      .trim()
      .slice(0)
      .split("");

    if (pasteText.length === otp.length) {
      setOtp(pasteText);
    }
  };

  // const updateHandler = (event, index) => {
  //   setOtp([
  //     ...otp.slice(0, index),
  //     event.target.value,
  //     ...otp.slice(index + 1),
  //   ]);
  // };

  useEffect(() => {
    inputRef.current?.focus();
  }, [activeOtpIndex]);

  if (location.state === null) {
    return;
  }

  return (
    <>
      {!loading && error && (
        <AlertMessage
          message={"Invalid OTP"}
          severity="error"
          open={open}
          handleClose={handleClose}
        />
      )}

      <BgBox>
        <CardUI>
          {/* MCC LOGO */}
          <MccLogo />

          <Grid container component="main">
            <Box
              component="form"
              onSubmit={otpVerificationHandler}
              xs={12}
              display="flex"
              gap="52px"
              flexDirection="column"
              width="100%"
            >
              <Box>
                <HeaderText
                  variant="h3"
                  fontSize={34}
                  textContent="Enter the 6 digit code"
                  lineHeight="24px"
                  textAlign="center"
                />
                <Typography
                  variant="body1"
                  className="d-flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize={16}
                  mt="16px"
                  color="#566875"
                >
                  We've sent you a six digit code on your email
                </Typography>
              </Box>

              <Box width="100%" display="flex" alignItems="center" gap="16px">
                {otp.map((s, index) => (
                  <TextField
                    key={index}
                    inputRef={index === activeOtpIndex ? inputRef : null}
                    variant="outlined"
                    className={classes.otp_box}
                    type="number"
                    value={s}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    onChange={otpChangeHandler}
                    onKeyDown={(e) => keyDownHandler(e, index)}
                    onPaste={pasteOtpHandler}
                    sx={{
                      "& input[type=number]": {
                        textAlign: "center",
                        "-moz-appearance": "textfield", // Firefox
                        "&::-webkit-outer-spin-button": {
                          "-webkit-appearance": "none", // Chrome, Safari, Edge
                          margin: 0,
                        },
                        "&::-webkit-inner-spin-button": {
                          "-webkit-appearance": "none", // Chrome, Safari, Edge
                          margin: 0,
                        },
                      },
                    }}

                    // onInput={(e) => updateHandler(e, index)}
                  />
                ))}
              </Box>

              <LoadingButton
                fullWidth
                loading={loading}
                type="submit"
                variant="contained"
                sx={{ padding: "8px 22px", fontSize: 15 }}
              >
                Continue
              </LoadingButton>

              {/* <Button
        className="w-100"
        fontSize="15px"
        color="primary"
        
        title="Sign In"
        variant="contained"
        width="100%"
        height="48px"
        // disabled={!fieldVals?.email || !fieldVals?.password} // Disabling it off on purpose
        // onClick={() => onSignIn()}
        // loading={signInLoading}
        loadingColor="primary"
      /> */}
            </Box>
          </Grid>
        </CardUI>
      </BgBox>
    </>
  );
};

export default OtpVerification;
